// src/components/VaccineProductionReceiveList.js
import React, { useState, useEffect } from "react";
import {
  Box,
  VStack,
  HStack,
  Spinner,
  Text,
  Input,
  Heading,
  InputGroup,
  InputLeftElement,
} from "@chakra-ui/react";
import { useNavigate, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileLines, faSearch } from "@fortawesome/free-solid-svg-icons";
import DataTable from "react-data-table-component";
import { tableCustomStyles } from "../themes/TableStyles";
import apiService from "../services/ApiService";
import { useAuth } from "../context/AuthContext";
import * as IconButton from "../themes/IconButtonSet";
import { StandardPageTitle, StandardHeader } from "../themes/StandardHeader";

const formatReceiveDate = (dateString) => {
  if (!dateString) {
    return "";
  }
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  return date.toLocaleDateString("th-TH", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
};

const getBudgetTypeLabel = (budgetType) => {
  switch (budgetType) {
    case "Prevention":
      return "งบป้องกัน";
    case "Distribution":
      return "งบจำหน่าย";
    default:
      return budgetType || "";
  }
};

const VaccineProductionReceiveList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { sessionId } = useAuth();
  const [receives, setReceives] = useState([]);
  const [filteredReceives, setFilteredReceives] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (!sessionId) {
      navigate("/login", { state: { from: location.pathname } });
    }
  }, [sessionId, navigate, location.pathname]);

  const columns = [
    {
      name: "เลขที่เอกสาร",
      selector: (row) => row.TransactionID,
      sortable: true,
      width: "300px",
    },
    {
      name: "วันที่รับ",
      selector: (row) => formatReceiveDate(row.ReceiveDate),
      sortable: true,
    },
    {
      name: "ประเภทงบประมาณ",
      selector: (row) => getBudgetTypeLabel(row.BudgetType),
      sortable: true,
    },
    {
      name: "ปีงบประมาณ",
      selector: (row) => row.BudgetYear,
      sortable: true,
    },
    {
      name: "การดำเนินการ",
      cell: (row) => (
        <IconButton.View
          icon={faFileLines}
          onClick={() => navigate(`/vaccineproductionreceive/${row.TransactionID}`)}
          label="รายละเอียด"
        />
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "250px",
    },
  ];

  const fetchWarehouses = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccineWarehousesList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setWarehouses(response.VaccineWarehousesList || []);
      }
    } catch (error) {
      console.error("Error fetching warehouses:", error);
    }
  };

  const fetchReceives = async () => {
    setIsLoading(true);
    try {
      await fetchWarehouses();
      const response = await apiService({
        method: "POST",
        url: "/getVaccineProductionReceivesList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        const data = response.VaccineProductionReceivesList || [];
        setReceives(data);
        setFilteredReceives(data);
      }
    } catch (error) {
      console.error("Error fetching receives:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (sessionId) {
      fetchReceives();
    }
  }, [sessionId]);

  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchTerm(value);

    if (!value) {
      setFilteredReceives(receives);
      return;
    }

    const searchTerms = value.split(" ").filter((term) => term.length > 0);
    const filtered = receives.filter((row) => {
      const warehouseName =
        warehouses.find((w) => w.WarehouseID === row.WarehouseID)?.WarehouseName ||
        row.WarehouseID ||
        "";
      const budgetTypeLabel = getBudgetTypeLabel(row.BudgetType).toLowerCase();
      const formattedReceiveDate = formatReceiveDate(row.ReceiveDate).toLowerCase();
      return searchTerms.every((term) =>
        [
          (row.TransactionID || "").toLowerCase(),
          warehouseName.toLowerCase(),
          budgetTypeLabel,
          (row.BudgetYear || "").toString().toLowerCase(),
          (row.Status || "").toLowerCase(),
          formattedReceiveDate,
        ].some((field) => field.includes(term))
      );
    });
    setFilteredReceives(filtered);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setFilteredReceives(receives);
  };

  const handleCreateNew = () => {
    navigate("/vaccineproductionreceive/new");
  };

  if (!sessionId) {
    return null;
  }

  return (
    <Box
      p={{ base: 4, md: 10, lg: 35 }}
      m={{ base: 2, md: 10, lg: 25 }}
      borderWidth={1}
      borderRadius="lg"
      boxShadow="lg"
    >
      <VStack spacing={6} align="start">
        <StandardHeader
          title="รายการรับวัคซีนจากฝ่ายผลิต"
          bgColor="iBlue.600"
          icon={faFileLines}
        />
        <HStack
          w="100%"
          justify="space-between"
          flexWrap={{ base: "wrap", md: "nowrap" }}
        >
          {receives.length > 0 && (
            <InputGroup
              w={{ base: "100%", md: "50%", lg: "30%" }}
              mb={{ base: 4, md: 0 }}
            >
              <InputLeftElement pointerEvents="none">
                <FontAwesomeIcon icon={faSearch} color="gray.300" />
              </InputLeftElement>
              <Input
                placeholder="ค้นหาใบรับวัคซีน..."
                value={searchTerm}
                onChange={handleSearch}
                colorScheme="iBlue"
                mt={1}
              />
              <IconButton.Clear onClick={handleClearSearch} ml={5} />
            </InputGroup>
          )}
          <IconButton.Add onClick={handleCreateNew} label="เพิ่มรายการใหม่" />
        </HStack>
        {isLoading ? (
          <Spinner size="md" />
        ) : receives.length === 0 ? (
          <Text fontSize="lg" color="gray.500" textAlign="center" w="100%">
            ยังไม่มีข้อมูลใบรับวัคซีน
          </Text>
        ) : filteredReceives.length === 0 ? (
          <Text fontSize="lg" color="gray.500" textAlign="center" w="100%">
            ไม่พบข้อมูลที่ค้นหา
          </Text>
        ) : (
          <Box w="100%" overflowX="auto">
            <DataTable
              columns={columns}
              data={filteredReceives}
              pagination
              highlightOnHover
              striped
              responsive
              customStyles={tableCustomStyles}
            />
          </Box>
        )}
      </VStack>
    </Box>
  );
};

export default VaccineProductionReceiveList;